import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  List,
  ListItem,
  Link,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { HomeHeader } from '../components/ui';
import { HomeFooter } from '../components/ui';

const BulletList = ({ items }) => (
  <List dense sx={{ listStyleType: 'disc', pl: 4, mb: 2 }}>
    {items.map((item, index) => (
      <ListItem
        key={index}
        sx={{
          display: 'list-item',
          py: 0.25,
          pl: 0,
        }}
      >
        <Typography variant="body1">{item}</Typography>
      </ListItem>
    ))}
  </List>
);

const NumberedList = ({ items }) => (
  <List component="ol" dense sx={{ listStyleType: 'decimal', pl: 4, mb: 2 }}>
    {items.map((item, index) => (
      <ListItem
        key={index}
        sx={{
          display: 'list-item',
          py: 0.25,
          pl: 0,
        }}
      >
        <Typography variant="body1">{item}</Typography>
      </ListItem>
    ))}
  </List>
);

const Section = ({ title, children }) => (
  <Box component="section" sx={{ mt: 4 }}>
    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const Terms = () => {
  const navigate = useNavigate();

  return (
    <>
      <HomeHeader />

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button variant="text" sx={{ mb: 2 }} onClick={() => navigate('/')}>
          Back to Home
        </Button>

        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
          Terms and Conditions
        </Typography>

        <Typography variant="subtitle2" gutterBottom>
          Version: 1.2
        </Typography>
        <Typography variant="subtitle2" gutterBottom>
          Effective Date: 15 June, 2026
        </Typography>
        <Typography variant="subtitle2" gutterBottom>
          Published at:{' '}
          <Link href="https://www.itclarifies.com/terms" target="_blank" rel="noopener noreferrer">
            https://www.itclarifies.com/terms
          </Link>
        </Typography>

        <Typography variant="body1" paragraph sx={{ mt: 3 }}>
          These Terms and Conditions govern access to and use of the Itclarifies learning platform by
          organizations, businesses, associations, public-sector entities, and other non-consumer
          customers. These Terms are not intended for consumers.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Section title="1. Parties">
          <Typography variant="body1" paragraph>
            These Terms are entered into between:
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            Itclarifies
          </Typography>
          <Typography variant="body1" paragraph>
            Itclarifies AB
            <br />
            Company registration number: 556967-9623
            <br />
            Address: Margretelund 1, 184 60 Åkersberga, Sweden
            <br />
            Email:{' '}
            <Link href="mailto:info@itclarifies.com">
              info@itclarifies.com
            </Link>
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
            Customer
          </Typography>
          <Typography variant="body1" paragraph>
            The organization, business, association, public-sector entity, or other legal person
            identified in an Order Form, subscription order, invoice, online registration, or other
            document referencing these Terms.
          </Typography>

          <Typography variant="body1" paragraph>
            Itclarifies and Customer are each referred to as a Party and together as the Parties.
          </Typography>
        </Section>

        <Section title="2. Agreement Structure and Order of Precedence">
          <Typography variant="body1" paragraph>
            The agreement between the Parties consists of the following documents:
          </Typography>
          <NumberedList
            items={[
              'Any order form, subscription confirmation, invoice, quote, statement of work, email confirmation, online registration, accepted subscription record, tenant activation confirmation, or other written or electronic record referencing these Terms or otherwise identifying the Customer and the applicable subscription, each an Order Form.',
              'Any applicable data processing agreement, data processing addendum, or similar document, the DPA.',
              'These Terms.',
              'Any service description, documentation, product policy, or usage policy made available by Itclarifies.',
            ]}
          />

          <Typography variant="body1" paragraph>
            If there is a conflict between documents, the following order applies:
          </Typography>
          <NumberedList
            items={[
              'The DPA, but only for personal-data-processing matters.',
              'The Order Form.',
              'These Terms.',
              'Documentation and policies.',
            ]}
          />

          <Typography variant="body1" paragraph>
            An Order Form may modify specific commercial terms, such as license period, price,
            number of active users, number of active games, support level, or permitted use. All other
            terms remain governed by these Terms unless expressly stated otherwise.
          </Typography>
        </Section>

        <Section title="3. Definitions">
          <Typography variant="body1" paragraph>
            In these Terms:
          </Typography>

          <Typography variant="body1" paragraph>
            <strong>Active Game</strong> means a game, identified by a game ID, that has been
            completed in at least one session, identified by a session ID, by a user acting in a player
            role during the applicable license period. Creator test runs, previews, sandbox tests,
            drafts, internal Itclarifies tests, and other non-player use do not count as Active Games.
          </Typography>

          <Typography variant="body1" paragraph>
            <strong>Active User</strong> means a user account associated with Customer’s organization
            or tenant that is not disabled, removed, terminated, or deactivated during the applicable
            license period. Unless the Order Form states otherwise, administrators, creators, hosts,
            game masters, and players count as Active Users. Itclarifies support or system accounts do
            not count as Active Users.
          </Typography>

          <Typography variant="body1" paragraph>
            <strong>Customer Content</strong> means all learning materials, PDFs, text, documents,
            presentations, images, media, instructions, prompts, source materials, files, and other
            content uploaded, submitted, imported, or otherwise provided to the Platform by or on
            behalf of Customer.
          </Typography>

          <Typography variant="body1" paragraph>
            <strong>Customer Data</strong> means Customer Content, Customer-specific Generated
            Content, user account data, session data, learning records, answers, results, scores,
            exports, configuration data, and other data submitted to or generated in Customer’s tenant
            or account.
          </Typography>

          <Typography variant="body1" paragraph>
            <strong>Customer-Specific Generated Content</strong> means games, questions, scenarios,
            answers, explanations, summaries, feedback, examination content, learning flows, and
            similar outputs generated by the Platform specifically for Customer based on Customer
            Content or Customer instructions.
          </Typography>

          <Typography variant="body1" paragraph>
            <strong>Documentation</strong> means usage instructions, technical information, help
            materials, policies, or other materials made available by Itclarifies in relation to the
            Platform.
          </Typography>

          <Typography variant="body1" paragraph>
            <strong>Fees</strong> means the fees specified in the applicable Order Form.
          </Typography>

          <Typography variant="body1" paragraph>
            <strong>License Period</strong> means the subscription or license period specified in the
            Order Form.
          </Typography>

          <Typography variant="body1" paragraph>
            <strong>Order Form</strong> means any order form, subscription confirmation, invoice, quote, 
            statement of work, email confirmation, online registration, accepted subscription record, 
            tenant activation confirmation, or other written or electronic record referencing these Terms 
            or otherwise identifying the Customer and the applicable subscription, pricing, license period, 
            tenant, license limits, or commercial terms.
          </Typography>

          <Typography variant="body1" paragraph>
            <strong>Platform</strong> means the Itclarifies learning platform, including the software,
            user interface, backend, APIs, quiz engine, simulation engine where applicable, game
            creation tools, game session tools, reporting tools, administrative functionality, and
            related services made available by Itclarifies.
          </Typography>

          <Typography variant="body1" paragraph>
            <strong>Platform IP</strong> means the Platform and all software, source code, object code,
            algorithms, workflows, templates, prompts, game mechanics, scoring logic, UI/UX,
            infrastructure, databases, documentation, trademarks, service marks, trade names,
            know-how, and other technology or materials owned or controlled by Itclarifies, excluding
            Customer Content.
          </Typography>

          <Typography variant="body1" paragraph>
            <strong>Services</strong> means access to the Platform and related support, hosting,
            maintenance, and other services provided by Itclarifies under an Order Form.
          </Typography>

          <Typography variant="body1" paragraph>
            <strong>Tenant</strong> means Customer’s private or organization-specific environment on
            the Platform, as further described in the applicable Order Form.
          </Typography>
        </Section>

        <Section title="4. B2B Scope and Acceptance">
          <Typography variant="body1" paragraph>
            The Platform is offered only to organizations, businesses, associations, public-sector
            entities, and other legal persons acting for professional or internal organizational
            purposes.
          </Typography>

          <Typography variant="body1" paragraph>
            Customer accepts these Terms, the applicable DPA, and the applicable subscription terms
            when Customer, its administrator, or its authorized representative:
          </Typography>

          <NumberedList
            items={[
              'Accepts a quote, subscription confirmation, or invoice referencing these Terms.',
              'Pays an invoice for the Services.',
              'Requests or authorizes Itclarifies to create, configure, or activate a tenant or subscription.',
              'Accesses or uses the Platform.',
              'Otherwise confirms acceptance in writing, by email, or through an electronic process.',
            ]}
          />

          <Typography variant="body1" paragraph>
            The person accepting on behalf of Customer represents that they are authorized to bind
            Customer.
          </Typography>

          <Typography variant="body1" paragraph>
            If Customer does not agree to these Terms or the DPA, Customer must not access or use the
            Platform.
          </Typography>
        </Section>

        <Section title="5. Access to the Platform">
          <Typography variant="body1" paragraph>
            Subject to Customer’s compliance with these Terms and payment of all applicable Fees,
            Itclarifies grants Customer a limited, non-exclusive, non-transferable, non-sublicensable
            right during the License Period to access and use the Platform for Customer’s internal
            training, learning, knowledge-sharing, and examination purposes, as specified in the
            applicable Order Form.
          </Typography>

          <Typography variant="body1" paragraph>
            Unless expressly agreed in an Order Form, Customer may not use the Platform:
          </Typography>
          <NumberedList
            items={[
              'To provide services to third parties.',
              'As a white-label or reseller service.',
              'To operate a competing product or service.',
              'For public-facing training or certification services.',
              'For regulated examination, professional licensing, or legally binding certification decisions.',
              'For purposes outside Customer’s own internal organization.',
            ]}
          />
        </Section>

        <Section title="6. Tenant, Organization, Spaces, and Access Management">
          <Typography variant="body1" paragraph>
            Where the Order Form includes a private Tenant, Itclarifies will make reasonable efforts to
            provide Customer with access to a Tenant configured for Customer’s organization.
          </Typography>

          <Typography variant="body1" paragraph>
            Customer is responsible for:
          </Typography>
          <NumberedList
            items={[
              'Managing its users and roles.',
              'Ensuring that only authorized users access the Platform.',
              'Removing or disabling users who no longer require access.',
              'Maintaining the confidentiality of login credentials.',
              'Ensuring that users comply with these Terms.',
              'Reviewing exports, results, and generated materials before relying on them.',
            ]}
          />

          <Typography variant="body1" paragraph>
            Itclarifies may rely on Customer’s administrators and authorized representatives as having
            authority to manage Customer’s Tenant, spaces, users, games, sessions, and data.
          </Typography>
        </Section>

        <Section title="7. License Limits">
          <Typography variant="body1" paragraph>
            The applicable Order Form will specify Customer’s license limits, including, where relevant:
          </Typography>
          <NumberedList
            items={[
              'Number of Tenants.',
              'Number of Active Users.',
              'Number of Active Games.',
              'Permitted use.',
              'Support level.',
              'Included functionality.',
              'License Period.',
              'Fees.',
            ]}
          />

          <Typography variant="body1" paragraph>
            Customer may not intentionally circumvent license limits, including by sharing accounts,
            misclassifying users, manipulating game IDs or session IDs, or using technical
            workarounds.
          </Typography>

          <Typography variant="body1" paragraph>
            Itclarifies may measure usage through Platform logs, system records, account data,
            session data, and other ordinary technical records.
          </Typography>

          <Typography variant="body1" paragraph>
            If Customer exceeds applicable license limits, Itclarifies may:
          </Typography>
          <NumberedList
            items={[
              'Notify Customer and request that usage be reduced.',
              'Invoice additional usage fees in accordance with the Order Form.',
              'Require an amended Order Form.',
              'Suspend additional use that exceeds the agreed limits.',
            ]}
          />

          <Typography variant="body1" paragraph>
            Unless the Order Form states otherwise, repeated sessions of the same game ID during the
            same License Period count as one Active Game.
          </Typography>
        </Section>

        <Section title="8. Customer Content Ownership">
          <Typography variant="body1" paragraph>
            Customer owns and retains all right, title, and interest in and to Customer Content.
            Nothing in these Terms transfers ownership of Customer Content to Itclarifies.
          </Typography>

          <Typography variant="body1" paragraph>
            Itclarifies does not claim ownership of Customer’s uploaded learning materials, including
            PDFs, text, documents, images, presentations, media, training materials, examination
            materials, internal policies, or other materials uploaded by or on behalf of Customer.
          </Typography>

          <Typography variant="body1" paragraph>
            Customer grants Itclarifies a limited, non-exclusive, worldwide, royalty-free license during
            the term of the agreement, and for any permitted post-termination export, backup,
            deletion, or legal-retention period, to host, store, copy, process, transmit, display,
            analyze, transform, and otherwise use Customer Content solely as necessary to:
          </Typography>
          <NumberedList
            items={[
              'Provide the Services to Customer.',
              'Generate Customer-Specific Generated Content for Customer.',
              'Operate, maintain, secure, troubleshoot, and support the Platform.',
              'Comply with applicable law.',
              'Prevent fraud, abuse, security incidents, or misuse.',
            ]}
          />

          <Typography variant="body1" paragraph>
            Itclarifies may not sell Customer Content. Itclarifies may not use Customer Content to
            train, fine-tune, or improve general-purpose AI models or third-party AI models, unless
            Customer has given prior written consent in a separate agreement. Itclarifies may not use
            Customer Content for another customer’s benefit.
          </Typography>
        </Section>

        <Section title="9. Customer-Specific Generated Content">
          <Typography variant="body1" paragraph>
            As between the Parties, Customer owns Customer-Specific Generated Content to the extent
            such content is based on Customer Content and is legally protectable, subject always to
            Itclarifies’ ownership of Platform IP.
          </Typography>

          <Typography variant="body1" paragraph>
            To the extent Itclarifies has any transferable rights in Customer-Specific Generated
            Content, Itclarifies assigns those rights to Customer. To the extent assignment is not
            possible, Itclarifies grants Customer a perpetual, non-exclusive, worldwide, royalty-free
            right to use, copy, adapt, export, and internally distribute Customer-Specific Generated
            Content for Customer’s internal training, learning, examination, and knowledge-management
            purposes.
          </Typography>

          <Typography variant="body1" paragraph>
            Customer-Specific Generated Content does not include Platform IP. For clarity, Itclarifies
            retains all rights in:
          </Typography>
          <NumberedList
            items={[
              'The Platform.',
              'Software and source code.',
              'Templates.',
              'Prompts and prompt structures.',
              'Scoring logic.',
              'Game mechanics.',
              'User interface elements.',
              'Workflows.',
              'Generic learning-design methods.',
              'Generalized know-how.',
              'Improvements to the Platform.',
              'Non-customer-specific features and functionality.',
            ]}
          />

          <Typography variant="body1" paragraph>
            Customer may continue to use exported Customer-Specific Generated Content after
            termination, but Customer has no right to access or use the Platform after termination
            unless agreed in a new Order Form.
          </Typography>
        </Section>

        <Section title="10. AI-Generated Output and Customer Review">
          <Typography variant="body1" paragraph>
            The Platform may use artificial intelligence, machine learning, language models,
            automation, or similar technologies to generate or assist with games, learning scenarios,
            questions, answers, explanations, summaries, feedback, and other content.
          </Typography>

          <Typography variant="body1" paragraph>
            Customer acknowledges that AI-generated content may be incomplete, inaccurate,
            misleading, biased, unsuitable, or otherwise flawed.
          </Typography>

          <Typography variant="body1" paragraph>
            Customer is responsible for reviewing, validating, approving, and, where necessary,
            correcting all Customer-Specific Generated Content before using it for training,
            examination, assessment, compliance, employment, certification, or decision-making
            purposes.
          </Typography>

          <Typography variant="body1" paragraph>
            Itclarifies does not warrant that Customer-Specific Generated Content is accurate,
            complete, pedagogically suitable, legally compliant, or appropriate for Customer’s intended
            use.
          </Typography>

          <Typography variant="body1" paragraph>
            The Platform is a learning-support and content-transformation tool. It is not a legally
            certified examination system, professional certification authority, HR decision system,
            legal compliance system, or substitute for professional judgment.
          </Typography>
        </Section>

        <Section title="11. Learning Results, Session Data, and Examination Exports">
          <Typography variant="body1" paragraph>
            Session results, answers, scores, rankings, feedback, learning records, examination
            exports, and similar records generated in Customer’s Tenant are Customer Data.
          </Typography>

          <Typography variant="body1" paragraph>
            Customer is responsible for determining how such data may be used within Customer’s
            organization, including for training, assessment, HR, compliance, examination, or reporting
            purposes.
          </Typography>

          <Typography variant="body1" paragraph>
            Itclarifies is not responsible for employment decisions, disciplinary decisions,
            certification decisions, compliance determinations, grading outcomes, or other decisions
            made by Customer based on Platform data, exports, scores, or generated content.
          </Typography>

          <Typography variant="body1" paragraph>
            Where the Order Form includes export functionality, Itclarifies will make the relevant
            session results available for export through the Platform’s standard export functionality.
          </Typography>
        </Section>

        <Section title="12. Customer Responsibilities">
          <Typography variant="body1" paragraph>
            Customer is responsible for ensuring that:
          </Typography>
          <NumberedList
            items={[
              'It has all necessary rights, permissions, licenses, and legal bases to upload and use Customer Content.',
              'Customer Content does not infringe third-party intellectual-property rights, confidentiality obligations, trade secrets, privacy rights, or other rights.',
              'Customer Content and Customer’s use of the Platform comply with applicable law.',
              'Users are informed of relevant internal rules, privacy notices, and policies.',
              'Generated content is reviewed before use.',
              'Personal data is processed lawfully.',
              'Special categories of personal data are not uploaded unless expressly agreed in writing.',
              'The Platform is used only for permitted purposes.',
            ]}
          />

          <Typography variant="body1" paragraph>
            Customer may not upload or process through the Platform:
          </Typography>
          <NumberedList
            items={[
              'Unlawful, infringing, defamatory, harassing, discriminatory, or harmful content.',
              'Malware, viruses, or malicious code.',
              'Content that violates third-party rights.',
              'Classified information.',
              'Highly sensitive business secrets unless explicitly agreed.',
              'Medical records, patient data, or health data unless explicitly agreed.',
              'Special categories of personal data under GDPR unless explicitly agreed.',
              'Personal data relating to children unless explicitly agreed.',
              'Content that Customer is not authorized to use.',
            ]}
          />
        </Section>

        <Section title="13. Prohibited Use">
          <Typography variant="body1" paragraph>
            Customer and its users may not:
          </Typography>
          <NumberedList
            items={[
              'Reverse engineer, decompile, disassemble, or attempt to derive source code from the Platform.',
              'Copy or reproduce the Platform except as expressly permitted.',
              'Scrape, crawl, or harvest Platform data.',
              'Bypass or attempt to bypass access controls, license limits, tenant boundaries, security controls, or usage restrictions.',
              'Interfere with the Platform’s operation.',
              'Perform penetration testing, vulnerability scanning, load testing, or security testing without Itclarifies’ prior written consent.',
              'Use the Platform to develop or train a competing product or service.',
              'Sell, sublicense, rent, lease, assign, distribute, or otherwise make the Platform available to third parties.',
              'Use the Platform in violation of applicable law.',
              'Use the Platform to generate unlawful, misleading, discriminatory, or harmful content.',
            ]}
          />
        </Section>

        <Section title="14. Personal Data and Data Protection">
          <Typography variant="body1" paragraph>
            The Parties acknowledge that the Platform may process personal data, including user names,
            email addresses, user roles, organization affiliation, session participation, answers,
            scores, feedback, and learning results.
          </Typography>

          <Typography variant="body1" paragraph>
            Unless otherwise agreed, Customer is the controller of personal data processed in
            Customer’s Tenant, and Itclarifies acts as processor on behalf of Customer.
          </Typography>

          <Typography variant="body1" paragraph>
            Where Itclarifies processes personal data as processor, such processing is governed by a
            separate DPA. Customer may not use the Platform for personal-data processing unless the
            Parties have entered into a DPA where required by applicable data-protection law.
          </Typography>

          <Typography variant="body1" paragraph>
            Customer is responsible for:
          </Typography>
          <NumberedList
            items={[
              'Having a lawful basis for processing personal data.',
              'Providing required privacy notices to users.',
              'Responding to data-subject requests where Customer is controller.',
              'Ensuring that uploaded content does not contain prohibited or excessive personal data.',
              'Determining appropriate retention periods.',
              'Ensuring that use of learning results, scores, and examination data complies with employment, privacy, and other applicable laws.',
            ]}
          />

          <Typography variant="body1" paragraph>
            Itclarifies will process personal data only in accordance with Customer’s documented
            instructions, these Terms, the DPA, and applicable law.
          </Typography>

          <Typography variant="body1" paragraph>
            Itclarifies may use subprocessors, including hosting, authentication, infrastructure, email,
            support, analytics, and AI service providers, as further described in the DPA or
            subprocessor list.
          </Typography>
        </Section>

        <Section title="15. Aggregated and Technical Data">
          <Typography variant="body1" paragraph>
            Itclarifies may collect and use aggregated, anonymized, statistical, diagnostic, and
            technical data relating to Platform usage, performance, security, reliability, and feature
            adoption, provided such data does not identify Customer, Customer’s users, or Customer
            Content.
          </Typography>

          <Typography variant="body1" paragraph>
            Itclarifies may use such aggregated and anonymized data to operate, maintain, secure,
            improve, and develop the Platform. This section does not permit Itclarifies to use Customer
            Content to train AI models.
          </Typography>
        </Section>

        <Section title="16. Confidentiality">
          <Typography variant="body1" paragraph>
            Each Party may receive confidential information from the other Party. Confidential
            information includes non-public business, technical, financial, product, security,
            operational, commercial, and legal information, whether disclosed orally, visually,
            electronically, in writing, through the Platform, or by any other means.
          </Typography>

          <Typography variant="body1" paragraph>
            Customer Content, Customer Data, Customer-Specific Generated Content, session results,
            examination results, and Tenant configurations are Customer’s confidential information.
            Platform IP, non-public product information, pricing, security information, technical
            architecture, source code, prompts, workflows, and product roadmap information are
            Itclarifies’ confidential information.
          </Typography>

          <Typography variant="body1" paragraph>
            The receiving Party shall:
          </Typography>
          <NumberedList
            items={[
              'Use confidential information only for the purposes of the agreement.',
              'Protect confidential information using reasonable care.',
              'Not disclose confidential information except to personnel, advisors, subcontractors, or subprocessors who need access for the purposes of the agreement and are bound by confidentiality obligations.',
              'Notify the disclosing Party of unauthorized disclosure where legally permitted and reasonably practicable.',
            ]}
          />

          <Typography variant="body1" paragraph>
            Confidentiality obligations do not apply to information that:
          </Typography>
          <NumberedList
            items={[
              'Is or becomes public without breach of the agreement.',
              'Was already lawfully known to the receiving Party.',
              'Is independently developed without use of the disclosing Party’s confidential information.',
              'Is lawfully received from a third party without confidentiality restrictions.',
              'Must be disclosed by law, court order, or authority, provided the receiving Party gives notice where legally permitted.',
            ]}
          />

          <Typography variant="body1" paragraph>
            Confidentiality obligations survive for five years after termination. Trade secrets remain
            protected for as long as they qualify as trade secrets under applicable law.
          </Typography>
        </Section>

        <Section title="17. Security">
          <Typography variant="body1" paragraph>
            Itclarifies will use commercially reasonable technical and organizational measures designed
            to protect Customer Data against unauthorized access, accidental loss, destruction,
            alteration, or disclosure.
          </Typography>

          <Typography variant="body1" paragraph>
            Customer acknowledges that no system can be guaranteed to be completely secure,
            uninterrupted, or error-free.
          </Typography>

          <Typography variant="body1" paragraph>
            Customer is responsible for:
          </Typography>
          <NumberedList
            items={[
              'Managing user access.',
              'Assigning appropriate roles.',
              'Protecting credentials.',
              'Using secure devices and networks.',
              'Promptly removing users who no longer need access.',
              'Notifying Itclarifies of suspected unauthorized access or security incidents.',
            ]}
          />

          <Typography variant="body1" paragraph>
            Itclarifies may suspend access to the Platform if it reasonably believes that continued
            access creates a security risk, legal risk, operational risk, or risk to other customers.
          </Typography>
        </Section>

        <Section title="18. Availability, Maintenance, and Changes to the Platform">
          <Typography variant="body1" paragraph>
            Itclarifies will use commercially reasonable efforts to make the Platform available during
            the License Period.
          </Typography>

          <Typography variant="body1" paragraph>
            Unless an Order Form expressly includes an SLA, Itclarifies does not provide any uptime
            commitment, service-credit remedy, or guaranteed availability level.
          </Typography>

          <Typography variant="body1" paragraph>
            Itclarifies may perform maintenance, updates, bug fixes, security changes, infrastructure
            changes, and feature changes from time to time. The Platform may be temporarily
            unavailable during maintenance or due to events beyond Itclarifies’ reasonable control.
          </Typography>

          <Typography variant="body1" paragraph>
            Itclarifies may modify, improve, replace, or remove features, provided that Itclarifies does
            not materially reduce the core functionality of the paid Services during the then-current
            License Period.
          </Typography>

          <Typography variant="body1" paragraph>
            Beta, experimental, preview, sandbox, or trial functionality may be changed or discontinued
            at any time and is provided as-is.
          </Typography>
        </Section>

        <Section title="19. Support">
          <Typography variant="body1" paragraph>
            Unless the Order Form states otherwise, Itclarifies provides email support for the
            Platform’s standard functionality.
          </Typography>

          <Typography variant="body1" paragraph>
            Support is provided during Swedish business days, excluding Swedish public holidays,
            during normal business hours in Sweden.
          </Typography>

          <Typography variant="body1" paragraph>
            Support does not include:
          </Typography>
          <NumberedList
            items={[
              'Custom development.',
              'Custom integrations.',
              'Customer-specific training workshops.',
              'Customer-side IT support.',
              'Browser, device, network, or security configuration issues.',
              'Third-party software issues.',
              'Data migration.',
              'Content review.',
              'Legal, HR, compliance, or pedagogical advice.',
              'Support for unsupported or unauthorized use.',
            ]}
          />
        </Section>

        <Section title="20. Fees, Invoicing, and Payment">
          <Typography variant="body1" paragraph>
            Customer shall pay the Fees specified in the applicable Order Form.
          </Typography>

          <Typography variant="body1" paragraph>
            Unless the Order Form states otherwise:
          </Typography>
          <NumberedList
            items={[
              'Fees are invoiced annually in advance.',
              'Payment is due 30 days from the invoice date.',
              'All prices are exclusive of VAT and other applicable taxes.',
              'Customer is responsible for VAT, taxes, bank fees, currency-conversion fees, and similar charges.',
              'Fees are non-refundable except as expressly stated in these Terms or required by mandatory law.',
            ]}
          />

          <Typography variant="body1" paragraph>
            If Customer exceeds agreed usage limits, Itclarifies may invoice additional fees in
            accordance with the applicable Order Form or Itclarifies’ then-current pricing.
          </Typography>

          <Typography variant="body1" paragraph>
            Late payments may accrue interest and statutory late-payment charges under applicable
            Swedish law. Itclarifies may suspend access to the Platform if undisputed Fees remain
            unpaid after written notice and a reasonable cure period of at least 10 days. Suspension
            does not relieve Customer of its obligation to pay Fees.
          </Typography>
        </Section>

        <Section title="21. Taxes">
          <Typography variant="body1" paragraph>
            All Fees are exclusive of VAT and other applicable taxes unless expressly stated otherwise.
          </Typography>

          <Typography variant="body1" paragraph>
            Customer shall pay all applicable taxes, duties, levies, and charges arising from the
            agreement, excluding taxes based on Itclarifies’ net income.
          </Typography>

          <Typography variant="body1" paragraph>
            If Customer is required by law to withhold or deduct taxes from payments, Customer shall
            increase the payment so that Itclarifies receives the full amount it would have received
            without such withholding or deduction, unless prohibited by law.
          </Typography>
        </Section>

        <Section title="22. Term and Renewal">
          <Typography variant="body1" paragraph>
            The agreement begins on the start date specified in the Order Form and continues for the
            License Period.
          </Typography>

          <Typography variant="body1" paragraph>
            Unless the Order Form states otherwise, the License Period is one year.
          </Typography>

          <Typography variant="body1" paragraph>
            Unless the Order Form states otherwise, the subscription renews automatically for
            successive one-year periods unless either Party gives written notice of non-renewal at least
            60 days before the end of the then-current License Period.
          </Typography>

          <Typography variant="body1" paragraph>
            Itclarifies may update pricing for renewal periods by giving notice before renewal.
          </Typography>
        </Section>

        <Section title="23. Termination for Breach">
          <Typography variant="body1" paragraph>
            Either Party may terminate the agreement if the other Party materially breaches the
            agreement and fails to cure the breach within 30 days after written notice.
          </Typography>

          <Typography variant="body1" paragraph>
            Itclarifies may suspend or terminate access immediately if Customer or its users:
          </Typography>
          <NumberedList
            items={[
              'Materially violate usage restrictions.',
              'Infringe Itclarifies’ intellectual-property rights.',
              'Create a security risk.',
              'Upload unlawful or harmful content.',
              'Violate applicable law.',
              'Fail to pay undisputed overdue Fees after notice.',
              'Use the Platform in a way that may harm Itclarifies, other customers, or the Platform.',
            ]}
          />

          <Typography variant="body1" paragraph>
            Customer remains responsible for Fees accrued before termination. If Customer terminates
            early without cause, Fees for the remaining License Period remain payable unless the Order
            Form states otherwise.
          </Typography>
        </Section>

        <Section title="24. Effects of Termination">
          <Typography variant="body1" paragraph>
            Upon termination or expiry:
          </Typography>
          <NumberedList
            items={[
              'Customer’s right to access and use the Platform ends.',
              'Customer shall stop using the Platform.',
              'Unpaid Fees become due.',
              'Each Party shall return or delete the other Party’s confidential information as required by the agreement, subject to legal, backup, archival, and compliance retention.',
              'Customer may export Customer Data during any export period made available by Itclarifies.',
            ]}
          />

          <Typography variant="body1" paragraph>
            Unless the Order Form or DPA states otherwise, Itclarifies will make commercially
            reasonable efforts to allow Customer to export available Customer Data for 30 days after
            termination or expiry.
          </Typography>

          <Typography variant="body1" paragraph>
            After the export period, Itclarifies may delete or anonymize Customer Data, subject to
            backups, legal obligations, dispute preservation, security logs, and other legitimate
            retention requirements. Backup copies may remain in secure backups until overwritten in
            the ordinary backup cycle.
          </Typography>

          <Typography variant="body1" paragraph>
            Sections intended to survive termination will survive, including sections on ownership,
            confidentiality, payment, data export and deletion, disclaimers, liability, indemnity,
            governing law, and dispute resolution.
          </Typography>
        </Section>

        <Section title="25. Intellectual Property">
          <Typography variant="body1" paragraph>
            Itclarifies owns and retains all right, title, and interest in and to Platform IP. No rights are
            granted to Customer except the limited rights expressly stated in the agreement.
          </Typography>

          <Typography variant="body1" paragraph>
            Customer may not remove, obscure, or alter copyright notices, trademark notices,
            proprietary notices, or other rights notices in the Platform.
          </Typography>

          <Typography variant="body1" paragraph>
            Customer owns Customer Content. Customer’s rights in Customer-Specific Generated
            Content are governed by Section 9. No implied licenses are granted.
          </Typography>
        </Section>

        <Section title="26. Feedback">
          <Typography variant="body1" paragraph>
            Customer may provide suggestions, ideas, requests, comments, or other feedback regarding
            the Platform.
          </Typography>

          <Typography variant="body1" paragraph>
            Itclarifies may use feedback without restriction or obligation, provided that Itclarifies does
            not disclose Customer’s confidential information or Customer Content in doing so.
          </Typography>

          <Typography variant="body1" paragraph>
            Feedback does not transfer ownership of Customer Content to Itclarifies.
          </Typography>
        </Section>

        <Section title="27. Custom Development and Professional Services">
          <Typography variant="body1" paragraph>
            Unless expressly agreed in a separate Order Form or statement of work, the Services do not
            include:
          </Typography>
          <NumberedList
            items={[
              'Custom development.',
              'Bespoke functionality.',
              'Customer-specific integrations.',
              'Data migration.',
              'Workshops.',
              'Training services.',
              'Customer-specific implementation services.',
              'Custom reporting.',
              'Custom security requirements.',
              'Functionality beyond the Platform’s standard functionality.',
            ]}
          />

          <Typography variant="body1" paragraph>
            If Itclarifies provides custom development or professional services, such work shall be
            governed by a separate Order Form or statement of work.
          </Typography>

          <Typography variant="body1" paragraph>
            Unless expressly agreed otherwise, Itclarifies owns all custom functionality, improvements,
            integrations, configurations, templates, workflows, software, and general know-how
            developed in connection with such services, excluding Customer Content and Customer’s
            confidential information.
          </Typography>

          <Typography variant="body1" paragraph>
            Customer receives only the rights expressly stated in the applicable Order Form or
            statement of work.
          </Typography>
        </Section>

        <Section title="28. Third-Party Services">
          <Typography variant="body1" paragraph>
            The Platform may depend on third-party services, including hosting providers, cloud
            infrastructure, authentication providers, AI providers, email providers, analytics providers,
            security providers, and support tools.
          </Typography>

          <Typography variant="body1" paragraph>
            Itclarifies may change third-party providers from time to time, subject to the DPA where
            personal data is involved.
          </Typography>

          <Typography variant="body1" paragraph>
            Itclarifies is not responsible for third-party service failures, outages, changes, or
            limitations beyond Itclarifies’ reasonable control, except to the extent required under the
            DPA or mandatory law.
          </Typography>

          <Typography variant="body1" paragraph>
            Customer shall comply with any third-party terms that apply directly to Customer’s use of
            integrated third-party services, where such terms have been notified to Customer.
          </Typography>
        </Section>

        <Section title="29. Publicity">
          <Typography variant="body1" paragraph>
            Itclarifies may not use Customer’s name, logo, trademarks, or identifying information in
            marketing, website references, press releases, case studies, or public customer lists without
            Customer’s prior written consent.
          </Typography>

          <Typography variant="body1" paragraph>
            Customer may not use Itclarifies’ name, logo, or trademarks without Itclarifies’ prior
            written consent, except to identify Itclarifies as the provider of the Platform internally
            within Customer’s organization.
          </Typography>
        </Section>

        <Section title="30. Warranties and Disclaimers">
          <Typography variant="body1" paragraph>
            Each Party warrants that it has authority to enter into the agreement. Itclarifies warrants
            that it will provide the Services with reasonable care and skill.
          </Typography>

          <Typography variant="body1" paragraph>
            Except as expressly stated in these Terms or an Order Form, the Platform and Services are
            provided “as is” and “as available.”
          </Typography>

          <Typography variant="body1" paragraph>
            Itclarifies does not warrant that:
          </Typography>
          <NumberedList
            items={[
              'The Platform will be uninterrupted, error-free, or always available.',
              'All defects will be corrected.',
              'The Platform will meet all of Customer’s requirements.',
              'Generated content will be accurate, complete, lawful, unbiased, or suitable.',
              'Examination results will be valid for any specific legal, HR, compliance, certification, or educational purpose.',
              'The Platform will be compatible with all devices, browsers, systems, or networks.',
              'Customer Content will produce any particular learning outcome.',
            ]}
          />

          <Typography variant="body1" paragraph>
            Customer is responsible for evaluating whether the Platform and generated content are
            suitable for Customer’s intended use.
          </Typography>
        </Section>

        <Section title="31. Limitation of Liability">
          <Typography variant="body1" paragraph>
            To the maximum extent permitted by applicable law, neither Party shall be liable for:
          </Typography>
          <NumberedList
            items={[
              'Indirect damages.',
              'Consequential damages.',
              'Loss of profits.',
              'Loss of revenue.',
              'Loss of business.',
              'Loss of goodwill.',
              'Reputational damage.',
              'Loss of anticipated savings.',
              'Loss or corruption of data, except to the extent expressly provided in the DPA.',
              'Business interruption.',
              'Procurement of substitute services.',
            ]}
          />

          <Typography variant="body1" paragraph>
            To the maximum extent permitted by applicable law, Itclarifies’ total aggregate liability
            arising out of or in connection with the agreement shall not exceed the Fees paid by
            Customer to Itclarifies under the applicable Order Form during the 12 months immediately
            preceding the event giving rise to the claim.
          </Typography>

          <Typography variant="body1" paragraph>
            If the claim arises during the first 12 months of the agreement, the cap shall equal the
            Fees paid or payable for the first 12 months under the applicable Order Form.
          </Typography>

          <Typography variant="body1" paragraph>
            The liability cap does not limit:
          </Typography>
          <NumberedList
            items={[
              'Customer’s obligation to pay Fees.',
              'Customer’s liability for unauthorized use of the Platform.',
              'Customer’s breach of Itclarifies’ intellectual-property rights.',
              'Customer’s indemnity obligations relating to Customer Content.',
              'Liability for fraud, intentional misconduct, or gross negligence.',
              'Liability that cannot be limited under mandatory law.',
            ]}
          />

          <Typography variant="body1" paragraph>
            Any liability relating to personal data shall be governed by the DPA to the extent the DPA
            contains specific liability provisions.
          </Typography>
        </Section>

        <Section title="32. Indemnity">
          <Typography variant="body1" paragraph>
            Customer shall defend, indemnify, and hold harmless Itclarifies from and against claims,
            damages, liabilities, costs, and expenses arising from:
          </Typography>
          <NumberedList
            items={[
              'Customer Content.',
              'Customer’s lack of rights to upload or use Customer Content.',
              'Customer’s use of the Platform in violation of these Terms.',
              'Customer’s violation of applicable law.',
              'Customer’s use of generated content for employment, certification, disciplinary, compliance, or legally significant decisions.',
              'Customer’s breach of third-party rights.',
            ]}
          />

          <Typography variant="body1" paragraph>
            Itclarifies shall defend Customer against third-party claims alleging that the Platform, as
            provided by Itclarifies and used in accordance with the agreement, infringes a third party’s
            intellectual-property rights.
          </Typography>

          <Typography variant="body1" paragraph>
            Itclarifies’ IP indemnity does not apply to claims arising from:
          </Typography>
          <NumberedList
            items={[
              'Customer Content.',
              'Customer-Specific Generated Content.',
              'Customer’s modifications.',
              'Combination with non-Itclarifies products or services.',
              'Use outside the scope of the agreement.',
              'Continued use after Itclarifies has provided a non-infringing alternative.',
              'Open-source or third-party components not controlled by Itclarifies.',
            ]}
          />

          <Typography variant="body1" paragraph>
            If the Platform becomes, or in Itclarifies’ opinion is likely to become, subject to an
            infringement claim, Itclarifies may:
          </Typography>
          <NumberedList
            items={[
              'Procure the right for Customer to continue using the Platform.',
              'Modify the Platform to make it non-infringing.',
              'Replace the Platform with substantially similar functionality.',
              'Terminate the affected Order Form and refund prepaid unused Fees for the affected period.',
            ]}
          />

          <Typography variant="body1" paragraph>
            This section states Customer’s exclusive remedy for third-party intellectual-property
            infringement claims relating to the Platform.
          </Typography>
        </Section>

        <Section title="33. Suspension">
          <Typography variant="body1" paragraph>
            Itclarifies may suspend Customer’s or a user’s access to the Platform if Itclarifies
            reasonably believes that:
          </Typography>
          <NumberedList
            items={[
              'Customer has failed to pay undisputed overdue Fees after notice.',
              'Customer or a user is violating the agreement.',
              'Customer or a user creates a security, legal, operational, or reputational risk.',
              'Suspension is necessary to protect the Platform, Itclarifies, Customer, other customers, users, or third parties.',
              'Suspension is required by law, court order, authority, or third-party provider.',
            ]}
          />

          <Typography variant="body1" paragraph>
            Itclarifies will use reasonable efforts to notify Customer of suspension unless prohibited by
            law or where immediate action is required. Itclarifies will restore access when the cause of
            suspension has been resolved, unless termination is justified.
          </Typography>
        </Section>

        <Section title="34. Changes to These Terms">
          <Typography variant="body1" paragraph>
            Itclarifies may update these Terms from time to time.
          </Typography>

          <Typography variant="body1" paragraph>
            Material changes will not materially reduce Customer’s rights during an active License
            Period unless:
          </Typography>
          <NumberedList
            items={[
              'Required by law.',
              'Required for security reasons.',
              'Required by third-party provider changes.',
              'Necessary to prevent abuse or misuse.',
              'Accepted by Customer.',
            ]}
          />

          <Typography variant="body1" paragraph>
            Material commercial changes normally apply from the next renewal period. Itclarifies will
            use reasonable efforts to notify Customer of material changes.
          </Typography>

          <Typography variant="body1" paragraph>
            Continued use of the Platform after the effective date of updated Terms constitutes
            acceptance of the updated Terms, where legally permitted.
          </Typography>
        </Section>

        <Section title="35. Force Majeure">
          <Typography variant="body1" paragraph>
            Neither Party is liable for delay or failure to perform caused by events beyond its
            reasonable control, including natural disasters, war, terrorism, strikes, labor disputes,
            government action, power failures, internet failures, cloud-provider failures, cyberattacks,
            denial-of-service attacks, epidemics, pandemics, or other events outside the affected
            Party’s reasonable control.
          </Typography>

          <Typography variant="body1" paragraph>
            Payment obligations are not excused by force majeure.
          </Typography>
        </Section>

        <Section title="36. Assignment and Subcontracting">
          <Typography variant="body1" paragraph>
            Customer may not assign the agreement without Itclarifies’ prior written consent, except to
            a successor in connection with a merger, acquisition, corporate reorganization, or sale of
            substantially all assets, provided that the successor is not a competitor of Itclarifies and
            agrees to be bound by the agreement.
          </Typography>

          <Typography variant="body1" paragraph>
            Itclarifies may assign the agreement to an affiliate or successor in connection with a
            merger, acquisition, corporate reorganization, or sale of substantially all assets.
          </Typography>

          <Typography variant="body1" paragraph>
            Itclarifies may use subcontractors and subprocessors to provide the Services, subject to the
            DPA where personal data is involved.
          </Typography>
        </Section>

        <Section title="37. Notices">
          <Typography variant="body1" paragraph>
            Notices under the agreement must be given in writing and sent by email, registered mail,
            courier, or through the Platform.
          </Typography>

          <Typography variant="body1" paragraph>
            Notices to Itclarifies shall be sent to:
            <br />
            Itclarifies AB
            <br />
            Margretelund 1, 184 60 Åkersberga, Sweden
            <br />
            Email:{' '}
            <Link href="mailto:info@itclarifies.com">
              info@itclarifies.com
            </Link>
          </Typography>

          <Typography variant="body1" paragraph>
            Notices to Customer shall be sent to the contact details in the Order Form, Customer’s
            account, or Customer’s administrative contact in the Platform.
          </Typography>

          <Typography variant="body1" paragraph>
            Notices by email are deemed received on the next business day after sending, unless the
            sender receives a delivery-failure notice.
          </Typography>
        </Section>

        <Section title="38. Governing Law and Disputes">
          <Typography variant="body1" paragraph>
            The agreement shall be governed by and construed in accordance with the laws of Sweden,
            without regard to conflict-of-law rules.
          </Typography>

          <Typography variant="body1" paragraph>
            The Parties shall first attempt to resolve disputes through good-faith negotiations.
          </Typography>

          <Typography variant="body1" paragraph>
            If a dispute cannot be resolved through negotiation, the dispute shall be finally settled by
            the Swedish courts, with Stockholm District Court as the court of first instance.
          </Typography>

          <Typography variant="body1" paragraph>
            The language of the agreement is English. If the agreement is translated into another
            language, the English version shall prevail unless the Parties expressly agree otherwise in
            writing.
          </Typography>
        </Section>

        <Section title="39. Miscellaneous">
          <Typography variant="body1" paragraph>
            The agreement constitutes the entire agreement between the Parties regarding the Services
            and supersedes prior discussions, proposals, quotes, negotiations, and understandings
            regarding the same subject matter.
          </Typography>

          <Typography variant="body1" paragraph>
            No waiver is effective unless made in writing. Failure to enforce a right does not constitute
            a waiver.
          </Typography>

          <Typography variant="body1" paragraph>
            If any provision is held invalid or unenforceable, the remaining provisions remain in effect,
            and the invalid provision shall be replaced by a valid provision that most closely reflects
            the original commercial intent.
          </Typography>

          <Typography variant="body1" paragraph>
            The Parties are independent contractors. Nothing in the agreement creates a partnership,
            joint venture, employment relationship, franchise, or agency relationship.
          </Typography>

          <Typography variant="body1" paragraph>
            Customer may not set off amounts owed under the agreement against claims Customer may
            have against Itclarifies unless required by mandatory law or agreed in writing.
          </Typography>
        </Section>

        
        <Section title="Contact">
          <Typography variant="body1" paragraph>
            For general questions, contact{' '}
            <Link href="mailto:info@itclarifies.com">
              info@itclarifies.com
            </Link>
            . For support, contact{' '}
            <Link href="mailto:support@itclarifies.com">
              support@itclarifies.com
            </Link>
            .
          </Typography>
        </Section>

        <Typography variant="h6" gutterBottom sx={{ mt: 5, fontWeight: 700 }}>
          Change Log / Version History
        </Typography>

        <Table sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Version</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Summary of Changes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>1.2</TableCell>
              <TableCell>2026-06-15</TableCell>
              <TableCell>
                Added definition of Order Form and subscription acceptance, clarified B2B
                acceptance mechanisms. Aligned with the Data Processing Agreement.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>1.1</TableCell>
              <TableCell>2026-05-04</TableCell>
              <TableCell>
                Replaced beta trial terms with business-to-business Terms and Conditions for
                organizations and businesses.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>1.0</TableCell>
              <TableCell>2025-06-27</TableCell>
              <TableCell>Initial release of Terms and Conditions for Beta Program.</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Container>

      <HomeFooter />
    </>
  );
};

export default Terms;